/**
 * Confirmation Modal Component
 *
 * Accessible modal for confirming destructive actions.
 * Replaces native confirm() with a styled, accessible alternative.
 */

import { useEffect, useRef } from 'react';
import { AlertTriangle, X } from 'lucide-react';

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning';
  isLoading?: boolean;
}

export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
  isLoading = false,
}: ConfirmModalProps) {
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Focus management
  useEffect(() => {
    if (isOpen) {
      // Focus the cancel button when modal opens (safer default)
      confirmButtonRef.current?.focus();
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isLoading) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isLoading, onClose]);

  // Trap focus within modal
  useEffect(() => {
    if (!isOpen) return;

    const modal = modalRef.current;
    if (!modal) return;

    const focusableElements = modal.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTabKey);
    return () => document.removeEventListener('keydown', handleTabKey);
  }, [isOpen]);

  if (!isOpen) return null;

  const variantStyles = {
    danger: {
      icon: 'bg-error/10 text-error',
      button: 'bg-error hover:bg-error/90 focus-visible:ring-error',
    },
    warning: {
      icon: 'bg-accent/10 text-accent-dark',
      button: 'bg-accent hover:bg-accent/90 focus-visible:ring-accent',
    },
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
      aria-describedby="confirm-modal-description"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={isLoading ? undefined : onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className="relative w-full max-w-md rounded-xl bg-bg-primary p-6 shadow-warm-lg"
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          disabled={isLoading}
          aria-label="Close"
          className="
            absolute top-4 right-4 p-1 rounded-lg
            text-text-tertiary hover:text-text-primary hover:bg-bg-secondary
            transition-colors
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
            disabled:opacity-50 disabled:cursor-not-allowed
          "
        >
          <X className="h-5 w-5" />
        </button>

        {/* Icon */}
        <div
          className={`
            mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4
            ${variantStyles[variant].icon}
          `}
        >
          <AlertTriangle className="h-6 w-6" aria-hidden="true" />
        </div>

        {/* Content */}
        <h2
          id="confirm-modal-title"
          className="text-lg font-semibold text-text-primary text-center mb-2"
        >
          {title}
        </h2>
        <p
          id="confirm-modal-description"
          className="text-text-secondary text-center mb-6"
        >
          {message}
        </p>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="
              flex-1 px-4 py-2.5 rounded-lg
              text-sm font-medium text-text-primary
              bg-bg-secondary hover:bg-bg-tertiary
              transition-colors
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
              disabled:opacity-50 disabled:cursor-not-allowed
            "
          >
            {cancelText}
          </button>
          <button
            ref={confirmButtonRef}
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className={`
              flex-1 px-4 py-2.5 rounded-lg
              text-sm font-medium text-white
              transition-colors
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
              disabled:opacity-50 disabled:cursor-not-allowed
              ${variantStyles[variant].button}
            `}
          >
            {isLoading ? 'Deleting...' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
